import React, { useState, useEffect, useCallback } from "react";
import {
  PieChart,
  Pie,
  Tooltip,
  Cell,
  ResponsiveContainer,
  Sector,
  Legend,
} from "recharts";
import { convertToInternationalCurrencySystem, isMobile } from "../../utils/utils";
import PieTooltip from "./PieTooltip";
export default function RechartArea({
  title,
  getData,
}: {
  title: any;
  getData: () => void;
}) {
  const COLORS = [
    "#3756CD",
    "#3756cde6",
    "#3756cdbf",
    "#3756cd99",
    "#3756cd73",
    "#3756cd66",
    "#3756cd4d",
    "#3756cd26",
    "#3755cd1a",
  ];
  const TokenColor: { [key: string]: string } = {
    WBNB: "#F3BA2F",
    BNB: "#F0B90B",
    WBTC: "#F7931A",
    WMATIC: "#2BBDF7",
    amUSDC: "#2775CA",
    BUSD: "#F0B90B",
    USDT: "#26A17B",
    CAKE: "#24C7D6",
    DERI: "#3756CD",
    TBUSD: "#F0B90B",
    SXP: "#FF6E26",
    ETH: "#444444",
    LTC: "#345D9D",
    XRP: "#11AAE2",
    BTCB: "#F7931A",
    ADA: "#3468D1",
    MATIC: "#2BBDF7",
    AAVE: "#B5519E",
    TUSD: "#1B5AFF",
    DAI: "#F5AC37",
    DOT: "#E6007A",
    LINK: "#2A5ADA",
    XVS: "#F2BC55",
    USDC: "#2775CA",
    TRX: "#FF060A",
    TDERI: "#3756CD",
    UST: "#5493F7",
    DOGE: "#C3A634",
    LUNA: "#FFD83D",
  };
  const [data, setData] = useState([]);
  const [lastItem, setLastItem] = useState({});
  const [parentRect, setParentRect] = useState<any>({});
  const [state, setState] = useState({
    activeIndex: 0,
  });
  const loadData = useCallback(async (getData: () => void) => {
    let res: any = await getData();
    let data = [];
    if (res.data) {
      data = res.data.sort((item1: any, item2: any) => {
        return item2.value - item1.value;
      });
    }
    data = data.map((d: any) => ({ name: d.token, value: Number(d.value) }));
    if (data.length > 0) {
      setLastItem(data[0]);
    }
    let other = data.slice(8, data.length);
    let name = {
      name: "OTHER",
      value: 0,
    };
    other = other.map((item: any) => {
      name["value"] += item.value;
      return name;
    });
    data = data.slice(0, 8);
    if (data.length === 8) {
      data.push(name);
    }
    setData(data);
  }, []);
  const renderLegend = (props: any) => {
    const { payload } = props;
    return (
      <ul className="token-list">
        {payload.map(
          (entry: any, index: any) =>
            index !== 8 && (
              <li key={`item-${index}`}>
                <span className="token-name">
                  {index + 1}. <div className={entry.value}></div>{" "}
                  <span className="token-name-text">{entry.value}</span>
                </span>{" "}
                {convertToInternationalCurrencySystem(entry.payload.value)}
              </li>
            )
        )}
      </ul>
    );
  };
  const onLeave = () => {
    setState({
      activeIndex: 0,
    });
  };
  const onPieEnter = (_: any, index: any) => {
    setState({
      activeIndex: index,
    });
  };
  const renderActiveShape = (props: any) => {
    const RADIAN = Math.PI / 180;
    const {
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
      percent,
      value,
    } = props;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const mx = cx + (outerRadius + 30) * cos;
    const my = cy + (outerRadius - 8) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;
    const textAnchor = cos >= 0 ? "start" : "end";

    return (
      <>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius - 5}
          outerRadius={outerRadius + 5}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={TokenColor[payload?.name]}
        />
        <text
          x={ex + (cos >= 0 ? 1 : -1) * -40}
          y={ey}
          dy={18}
          textAnchor={textAnchor}
          fill="#FFFFFF"
          className="percent"
        >
          {`${(percent * 100).toFixed(2)}%`}
        </text>
      </>
    );
  };
  useEffect(() => {
    const rect = document.querySelector(".graph")?.getBoundingClientRect();
    setParentRect(rect);
  }, []);
  useEffect(() => {
    if (getData) {
      loadData(getData);
    }
  }, [getData, loadData]);
  return (
    <ResponsiveContainer height={230}>
      <PieChart width={parentRect?.width}>
        <Pie
          dataKey="value"
          data={data}
          activeShape={renderActiveShape}
          activeIndex={state.activeIndex}
          cx={isMobile() ? "50%" : "30%"}
          cy="50%"
          outerRadius={100}
          innerRadius={75}
          fill="#8884d8"
          onMouseEnter={onPieEnter}
          onMouseLeave={onLeave}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} stroke="0" fill={COLORS[index]} />
          ))}
        </Pie>
        <Legend content={renderLegend} />
        <Tooltip
          position={{ x: 16, y: -92 }}
          cursor={true}
          content={<PieTooltip title={title} lastItem={lastItem} />}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}
